import type React from 'react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import html2canvas from 'html2canvas';

import type { ExportPdfRef } from './types/export-pdf';

interface PrintableChartProps extends React.PropsWithChildren {
  type?: 'svg' | 'canvas';
  className?: string;
  defaultDisplay?: string;
}

const waitForChartRender = (container: HTMLElement, type: 'svg' | 'canvas') => {
  return new Promise<void>((resolve) => {
    if (container.querySelector(type)) {
      resolve();
      return;
    }
    const observer = new MutationObserver(() => {
      if (container.querySelector(type)) {
        observer.disconnect();
        resolve();
      }
    });
    observer.observe(container, { childList: true, subtree: true });
  });
};

export const PrintableChart = forwardRef<ExportPdfRef, PrintableChartProps>(
  ({ children, className, type = 'svg', defaultDisplay = 'block' }, ref) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const [chartImage, setChartImage] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      startExportPdf: async () => {
        if (chartRef.current) {
          chartRef.current.style.display = 'block';

          await waitForChartRender(chartRef.current, type);

          const canvas = await html2canvas(chartRef.current, {
            backgroundColor: '#fff',
            scale: 2,
          });

          setChartImage(canvas.toDataURL('image/png'));

          // Make sure chart image fully rendered
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      },
      finishExportPdf: async () => {
        setChartImage(null);
        if (chartRef.current) {
          chartRef.current.style.display = defaultDisplay;
        }
      },
    }));

    if (chartImage) {
      return (
        <div>
          <img src={chartImage} alt="Chart snapshot" width="100%" />
        </div>
      );
    }

    return (
      <div ref={chartRef} className={className} style={{ display: defaultDisplay }}>
        {children}
      </div>
    );
  }
);
